import React, { useCallback, useEffect, useImperativeHandle, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import {
  Text,
  Flex,
  SimpleGrid,
  Box,
  Button,
  FormControl,
  FormLabel,
  FormErrorMessage,
  Input,
  Tooltip,
  Icon,
  IconButton,
  useColorModeValue,
} from "@chakra-ui/react";
import { FaTrash, FaPencilAlt } from "react-icons/fa";
import { useCustomToast } from "components/CustomToast/CustomToast";
import { TRAFFIC_VIOLATIONS_MAX_IMAGES } from "constants/config";
import { ImageEditorModal } from "views/Pages/TrafficViolation/ImageEditorModal";
import { optimizeImageBlob } from "utils/image-optimize";

export const TrafficViolationImagesForm = React.forwardRef(({ disabledFields = [false, false], ...props }, ref) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState({});
  const [editingImageId, setEditingImageId] = useState(null);
  const showToast = useCustomToast();
  const imageBg = useColorModeValue("white", "navy.900");
  const imageBorder = useColorModeValue("gray.200", "gray.400");

  const maxFiles = 1;
  const {
    isDisabled = false,
    translationKey = "traffic-violation.form",
    acceptFiles = ["image/jpeg", "image/jpg", "image/png", "image/bmp"],
    imageCount = +TRAFFIC_VIOLATIONS_MAX_IMAGES,
    requiredFirstImage = false,
    firstImageMissing = false,
    ...rest
  } = props;

  const formInfo = useMemo(() => {
    const formInfo = [];
    for (let i = 1; i <= imageCount; i++) {
      formInfo.push({
        id: `ait-image-${i}`,
        label: t(`${translationKey}.fields.ait-image.label`, { number: i }),
      });
    }
    return formInfo;
  }, [imageCount]);

  const clearFiles = useCallback((id) => {
    if (!id) {
      setFiles((prev) => {
        Object.values(prev).forEach((f) => f?.previewUrl && URL.revokeObjectURL(f.previewUrl));
        return {};
      });
      return;
    }
    setFiles(prev => {
      const current = prev[id];
      if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
      const newFiles = { ...prev };
      delete newFiles[id];
      return newFiles;
    });
  }, [setFiles]);

  const handleClear = useCallback((e, id) => {
    e.stopPropagation();
    clearFiles(id);
  }, [clearFiles]);

  const onError = useCallback((rejectedFiles) => {
    if (rejectedFiles?.length > maxFiles) {
      return showToast({ title: t("system.messages.errors.max-files"), status: "error" });
    }
    showToast({ title: t("system.messages.errors.invalid-file"), status: "error" });
  }, [maxFiles, showToast]);

  const onDropAccepted = useCallback((id, droppedFiles) => {
    if (droppedFiles.length <= 0) return;
    const file = droppedFiles[0];
    const previewUrl = URL.createObjectURL(file);
    setFiles(prev => ({
      ...prev,
      [id]: { id, name: file.name, file, previewUrl }
    }));
    optimizeImageBlob(file, 1920, 0.92).then((optimizedBlob) => {
      const baseName = (file.name || "image").replace(/\.[^.]+$/, "") || "image";
      const optimizedFile = new File([optimizedBlob], `${baseName}.jpg`, { type: "image/jpeg" });
      setFiles(prev => {
        const current = prev[id];
        if (current?.previewUrl) URL.revokeObjectURL(current.previewUrl);
        return {
          ...prev,
          [id]: { id, name: optimizedFile.name, file: optimizedFile, previewUrl: URL.createObjectURL(optimizedBlob) }
        };
      });
    }).catch(() => {});
  }, [setFiles]);

  const handleSaveEditedImage = useCallback((id, blob) => {
    const current = files[id];
    if (!current) return;
    const baseName = current.name.replace(/\.[^.]+$/, "") || "image";
    const editedFile = new File([blob], `${baseName}.jpg`, { type: "image/jpeg" });
    if (current.previewUrl) URL.revokeObjectURL(current.previewUrl);
    const previewUrl = URL.createObjectURL(blob);
    setFiles(prev => ({
      ...prev,
      [id]: { id, name: editedFile.name, file: editedFile, previewUrl }
    }));
    setEditingImageId(null);
  }, [files]);

  useEffect(() => {
    return () => {
      Object.values(files).forEach((f) => {
        if (f?.previewUrl) URL.revokeObjectURL(f.previewUrl);
      });
    };
  }, [files]);

  useImperativeHandle(ref, () => ({
    getFiles: () => files,
    getFilesList: () => Object.values(files),
  }));

  const { onFilesChange, ...restProps } = rest;
  useEffect(() => {
    onFilesChange?.(files);
  }, [files, onFilesChange]);

  return (
    <SimpleGrid spacing="30px" columns={{ sm: 1, lg: 2 }} { ...restProps }>
      {formInfo?.map((info, key) => {
        if (disabledFields[key]) return null;

        const isFirstImage = key === 0;
        const isSecondImage = key === 1;
        const isRequired = isFirstImage && requiredFirstImage;
        const showError = isFirstImage && requiredFirstImage && firstImageMissing;
        const firstImageHasFile = !!files["ait-image-1"]?.file;
        const dropzoneDisabled = isDisabled || (isSecondImage && !firstImageHasFile);

        return (
            <FormControl key={key} isRequired={isRequired} isInvalid={showError}>
              <FormLabel fontSize="xs" fontWeight="bold">
                {info.label}
              </FormLabel>
              <Dropzone
                  disabled={dropzoneDisabled}
                  maxFiles={maxFiles}
                  accept={acceptFiles}
                  onDropAccepted={files => onDropAccepted(info.id, files)}
                  onDropRejected={onError}
                  onError={onError}
              >
                {({ getRootProps, getInputProps }) => (
                    <Flex
                        align="center"
                        justify="center"
                        gap="3"
                        minH="72px"
                        px="3"
                        py="2"
                        cursor={dropzoneDisabled ? "not-allowed" : "pointer"}
                        opacity={dropzoneDisabled ? 0.6 : 1}
                        border="2px dashed"
                        borderRadius="8px"
                        bg={imageBg}
                        borderColor={imageBorder}
                        {...getRootProps({ className: "dropzone" })}
                    >
                      <Input id={info.id} {...getInputProps()} />

                      {files?.[info.id]?.previewUrl ? (
                        <>
                          <Box
                            as="img"
                            src={files[info.id].previewUrl}
                            alt={files[info.id].name}
                            w="56px"
                            h="56px"
                            borderRadius="6px"
                            objectFit="cover"
                            flexShrink={0}
                          />
                          <Button fontSize="sm" variant="no-effects" flex="1" minW="0" justifyContent="flex-start">
                            <Text fontWeight="normal" noOfLines={1} title={files[info.id].name}>
                              {files[info.id].name}
                            </Text>
                          </Button>
                          <Tooltip hasArrow label={t(`${translationKey}.fields.ait-image.edit-image`)}>
                            <IconButton
                              size="xs"
                              colorScheme="blue"
                              aria-label={t(`${translationKey}.fields.ait-image.edit-image`)}
                              onClick={e => { e.stopPropagation(); setEditingImageId(info.id); }}
                              icon={<Icon as={FaPencilAlt} />}
                              flexShrink={0}
                            />
                          </Tooltip>
                          <Tooltip hasArrow label={t("system.actions.clear")}>
                            <IconButton
                              size="xs"
                              colorScheme="red"
                              aria-label={t("system.actions.clear")}
                              onClick={e => handleClear(e, info.id)}
                              icon={<Icon as={FaTrash} />}
                              flexShrink={0}
                            />
                          </Tooltip>
                        </>
                      ) : (
                        <Button fontSize="sm" variant="no-effects">
                          <Text fontWeight="normal" color="gray.400">
                            {t("system.placeholders.file-dropzone")}
                          </Text>
                        </Button>
                      )}
                    </Flex>
                )}
              </Dropzone>
              {showError && (
                <FormErrorMessage>
                  {t(`${translationKey}.fields.ait-image.required-video-monitoring`)}
                </FormErrorMessage>
              )}
            </FormControl>
        );
      })}
      <ImageEditorModal
        isOpen={!!editingImageId}
        onClose={() => setEditingImageId(null)}
        imageFile={editingImageId ? files[editingImageId]?.file : null}
        translationKey={translationKey}
        onSave={(blob) => editingImageId && handleSaveEditedImage(editingImageId, blob)}
      />
    </SimpleGrid>
  );
});
